import classNames from 'classnames';
import { Icon, IconsEnum } from 'components/Icons';
import { useAmbienteContext } from 'context/Ambiente';
import { useInfoAggiuntivaSummaryByInfoTipoScommessaKey, useInfoTipo } from 'features/sport/hooks';
import { UpdateAllInfoAggPayload } from 'features/sport/types';
import { useAppDispatch, useTypedSelector } from 'lib/centralStore';
import { useEffect, useState, useCallback, useRef } from 'react';
import styles from './SportTableScommesseInfoAggSelector.module.scss';
import { selectSelectedAllInfoAgg, selectSelectedAllInfoAggByInfoTipoScommessaKey } from 'features/sport/selectors';
import { setSelectedAllInfoAgg } from 'features/sport/sportSlice';
import { SportsInfoAggSummaryDto } from 'types/swagger';
import { Portal } from '@radix-ui/react-portal';
import useWindowSize from 'hooks/useWindowSize';

type SportTableScommesseInfoAggSelectorProps = {
  infoTipoScommessaKey: string;
  header: string;
  width?: string | number;
};

export const SportTableScommesseInfoAggSelector = ({
  header,
  width,
  infoTipoScommessaKey,
}: SportTableScommesseInfoAggSelectorProps) => {
  const dispatch = useAppDispatch();
  const { isMobile } = useWindowSize();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedInfoAggHex = useTypedSelector((state) =>
    selectSelectedAllInfoAggByInfoTipoScommessaKey(state, infoTipoScommessaKey)
  );
  const infoAggSelector = useInfoAggiuntivaSummaryByInfoTipoScommessaKey(infoTipoScommessaKey);
  const { infoAggUpdateAllActionType } = useAmbienteContext();

  const raiseAction = useCallback(
    (selectedInfoAggHex?: string) => {
      if (!selectedInfoAggHex) return;

      const payload: UpdateAllInfoAggPayload = {
        infoTipoScommessaKey,
      };
      dispatch({ type: infoAggUpdateAllActionType, payload });
    },
    [dispatch, infoAggUpdateAllActionType, infoTipoScommessaKey]
  );

  const handleSelect = (infoAgg: SportsInfoAggSummaryDto) => {
    dispatch(setSelectedAllInfoAgg({ [infoTipoScommessaKey]: infoAgg }));
    raiseAction(infoAgg?.hexValue);
    setIsOpen(!isOpen);
  };

  return (
    <div className={styles.container} style={{ width: `${width}px` }} ref={containerRef}>
      <button type="button" className={styles.button} onClick={() => setIsOpen(!isOpen)}>
        <span className={styles.text}>{selectedInfoAggHex ? selectedInfoAggHex.displayValue : header}</span>
        <span className={styles.iconContainer}>
          <Icon
            iconId={IconsEnum.CHEVRON_DOWN}
            className={classNames(styles.icon, { [styles.rotate]: isOpen })}
            color="var(--color-neutral-01)"
          />
        </span>
      </button>
      {isOpen && (
        <Portal asChild container={isMobile ? document.body : containerRef.current}>
          <div className={classNames({ [styles.list]: !isMobile, [styles.mobileDropdown]: isMobile })}>
            <div className={styles.header}>
              <span>Scegli la tua quota</span>
              <button type="button" onClick={() => setIsOpen(false)} className={styles.button}>
                <Icon iconId={IconsEnum.CLOSE} className={styles.icon} />
              </button>
            </div>
            <ul>
              {infoAggSelector?.map((infoAgg) => {
                const isSelected = selectedInfoAggHex?.hexValue === infoAgg.hexValue;
                return (
                  <li
                    key={`${infoTipoScommessaKey}_${infoAgg.hexValue}`}
                    className={styles.item}
                    onClick={() => handleSelect(infoAgg)}
                  >
                    <span className={styles.check}>
                      {isSelected && (
                        <Icon iconId={IconsEnum.CHECK} className={styles.checkIcon} color="var(--color-primary)" />
                      )}
                    </span>
                    <span className={styles.label}>{infoAgg.displayValue}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        </Portal>
      )}
    </div>
  );
};
